import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import authService from '../services/authService';
import socketClient from '../services/socketClient';
import Input from './shared/Input';
import './Forms.css';

const JoinGame = () => {
  const [roomCode, setRoomCode] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { requireAuth, username } = useAuth();

  useEffect(() => {
    // Check if user has a token using the hook
    // Pass current path so user returns here after authentication
    requireAuth('/join-game');
  }, [requireAuth]);

  // Enhanced room code sanitization
  const sanitizeRoomCode = (input) => {
    return input
      .trim() // Remove leading/trailing spaces
      .toUpperCase() // Consistent case
      .replace(/[^A-Z0-9]/g, '') // Only letters and numbers
      .substring(0, 8); // Allow up to 8 characters (flexible length)
  };

  // Validate room code
  const validateRoomCode = (code) => {
    const cleanCode = code.trim();

    if (cleanCode.length < 4) {
      return 'Room code must be at least 4 characters long';
    }
    if (cleanCode.length > 8) {
      return 'Room code must be no more than 8 characters long';
    }
    if (!/^[A-Z0-9]+$/.test(cleanCode)) {
      return 'Room code can only contain letters and numbers';
    }
    return null;
  };

  const handleRoomCodeChange = (e) => {
    // Clean input in real-time as user types
    const sanitized = sanitizeRoomCode(e.target.value);
    setRoomCode(sanitized);
    setError(''); // Clear error when user types
  };

  const handleJoinRoom = async (isSpectator = false) => {
    // Final validation before joining
    const finalRoomCode = roomCode.trim();
    const validationError = validateRoomCode(finalRoomCode);

    if (validationError) {
      setError(validationError);
      return;
    }

    setIsJoining(true);
    setError('');

    try {
      // Connect to WebSocket server
      socketClient.connect();

      // Wait for connection
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Connection timeout - please try again'));
        }, 15000);

        if (socketClient.isSocketConnected()) {
          clearTimeout(timeout);
          resolve();
          return;
        }

        socketClient.on('connect', () => {
          clearTimeout(timeout);
          resolve();
        });

        socketClient.on('connect_error', (error) => {
          clearTimeout(timeout);
          reject(error);
        });
      });

      // Join room via socket
      await socketClient.joinRoom(
        finalRoomCode,
        username,
        authService.getToken(),
        null, // no settings when joining existing room
        isSpectator
      );

      localStorage.setItem('currentRoom', finalRoomCode);
      localStorage.setItem('isHost', 'false');
      localStorage.setItem('isSpectator', isSpectator ? 'true' : 'false');

      console.log(`Joining room ${finalRoomCode}${isSpectator ? ' as spectator' : ''}`);

      // Navigate to lobby
      navigate('/game-lobby');
    } catch (err) {
      console.error('Failed to join room:', err);
      setError(err.message || 'Failed to join room. Please check the code and try again.');
    } finally {
      setIsJoining(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isJoining && roomCode.trim().length >= 4) {
      handleJoinRoom();
    }
  };

  const handleChangeUsername = () => {
    // Reset usernameConfirmed flag so user can change their name
    const user = authService.getUser();
    if (user) {
      user.usernameConfirmed = false;
      localStorage.setItem('user', JSON.stringify(user));
    }
    // Redirect to username selection page
    navigate('/choose-username');
  };

  // Helper function to get character count display color
  const getCharCountColor = () => {
    const length = roomCode.length;
    if (length < 4) return '#999';
    if (length > 6) return '#ff6b6b';
    return '#28a745';
  };

  return (
    <div className="room-entry-container">
      <div className="room-entry-card">
        <div className="user-info">
          <span className="username-display">Playing as: {username}</span>
          <button className="change-username-btn" onClick={handleChangeUsername}>
            Change
          </button>
        </div>

        <h1 className="room-entry-title">Join Game</h1>
        <p className="room-entry-subtitle">Enter the room code to join an existing game</p>

        <div className="room-code-input-container">
          <div className="input-group">
            <Input
              type="text"
              value={roomCode}
              onChange={handleRoomCodeChange}
              onKeyPress={handleKeyPress}
              placeholder="ABCD12"
              maxLength={8}
              disabled={isJoining}
              autoFocus
              error={error}
              ariaLabel="Enter room code"
            />

            <div className="input-feedback">
              <div className="input-requirements">
                <span className="requirement">4-8 characters</span>
                <span className="requirement">Letters and numbers only</span>
              </div>

              <div className="char-counter">
                <span style={{ color: getCharCountColor() }}>{roomCode.length}/8</span>
              </div>
            </div>
          </div>

          <div className="join-buttons">
            <button
              className={`join-room-btn primary ${isJoining ? 'loading' : ''}`}
              onClick={() => handleJoinRoom(false)}
              disabled={isJoining || roomCode.trim().length < 4}
            >
              {isJoining ? 'Joining...' : 'Join as Player'}
            </button>
            <button
              className={`join-room-btn secondary ${isJoining ? 'loading' : ''}`}
              onClick={() => handleJoinRoom(true)}
              disabled={isJoining || roomCode.trim().length < 4}
            >
              {isJoining ? 'Joining...' : 'Join as Voyeur'}
            </button>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="room-code-examples">
          <h2>Examples of valid room codes:</h2>
          <div className="example-codes">
            <span className="example-code">ABCD</span>
            <span className="example-code">GAME123</span>
            <span className="example-code">FUN2024</span>
          </div>
        </div>

        <div className="back-link">
          <button onClick={() => navigate('/')} className="back-btn" disabled={isJoining}>
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default JoinGame;
